import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useSnackbar } from 'notistack';

import useHandleError from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

import type {
  AssetMappingData,
  AssetMappingUpload
} from './AssetMappingUploadDialog.types';

export const useDeleteAssetMapping = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return axiosClient.delete(
        `asset_ip_mappings/${selectedOrganization.label}`
      );
    },
    onSuccess: () => {
      enqueueSnackbar(`IP Tag Mapping Deleted`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: ['asset_ip_mappings', selectedOrganization.label]
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete IP tag mapping.');
    }
  });
};

export const useFetchAssetMapping = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const { isError, error, data, isLoading } = useQuery<AssetMappingData[]>({
    queryKey: ['asset_ip_mappings', selectedOrganization.label],
    queryFn: async () => {
      const url = `/asset_ip_mappings/${selectedOrganization.label}`;
      const response = await axiosClient.get(url);
      return response.data;
    },
    enabled: selectedOrganization?.label !== 'all'
  });

  if (isError) {
    handleError(error as AxiosError, 'Failure to fetch asset IP mappings.');
  }

  return {
    isLoading,
    data: data || [],
    hasData: data && data.length > 0
  };
};

export const useUploadAssetMapping = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mappingData: AssetMappingUpload) => {
      const file = mappingData.attachments[0];
      const mappingFile = { file };
      return axiosClient.post(
        `asset_ip_mappings/${selectedOrganization.label}/upload`,
        mappingFile,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json'
          }
        }
      );
    },
    onSuccess: () => {
      enqueueSnackbar(`IP Tag Mapping Uploaded`, {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: ['asset_ip_mappings', selectedOrganization.label]
      });
    },
    onError: (error: AxiosError) => {
      // Don't use handleError here, let the component handle the error display
      console.error('Error uploading IP tag mapping:', error);
    }
  });
};

export default {
  useDeleteAssetMapping,
  useFetchAssetMapping,
  useUploadAssetMapping
};
